import { memo, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../../../config/googleMaps";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Badge,
  Button,
  Combobox,
  confirmDialog,
  EmptyState,
  Input,
  Tabs,
  Textarea,
} from "../../../components/ui";
import { DocumentChecklist } from "../../../features/shipmentDocuments/DocumentChecklist";
import { DOCS_NS } from "../../../features/shipmentDocuments/meta";
import { EventsDetailModal } from "./EventsDetailModal";
import ShipmentMerchandiseCard from "./ShipmentMerchandiseCard";
import ShipmentUnitEditor from "./ShipmentUnitEditor";
import CarrierTrackingCard from "./CarrierTrackingCard";
import UnitAvatar from "../../../components/base/UnitAvatar";
import { getMapStyles } from "../../../components/map/mapStyles";
import {
  MARKER_SIZE,
  VEHICLE_STATUS_COLORS,
  VehicleMarkerGlyph,
  getMarkerShape,
} from "../../../components/map/vehicleMarkers";
import { STATUS_META, type StatusId } from "../../lite/components/Sidebar/statusMeta";
import { getTimeAgo } from "../../../utils/getDate";
import { useAddShipmentEvent } from "../../../features/shipments/mutations";
import { useShipmentTrackingLink } from "../../../features/shipments/trackingLink";
import {
  furthestProgressionType,
  isBackwardsEvent,
  SHIPMENT_EVENT_TYPES,
  shipmentPosition,
  shipmentStage,
  stageForEventType,
  type Shipment,
  type ShipmentEvent,
  type ShipmentEventType,
} from "../../../features/shipments/types";
import { getLastPosition } from "../../../socket";
import { useVehiclesSocketStore } from "../../../store/admin/vehiclesSocketStore";
import { useShipmentUnit, type ShipmentUnit } from "../hooks/useShipmentUnit";
import { UNIT_STATUS_TONES } from "../statusTones";
import { trackingLinkErrorMessage } from "../utils/trackingLinkError";
import {
  trackShipmentStageChanged,
  trackShipmentStageChangeAbandoned,
  trackShipmentStageChangeFailed,
  trackTrackingLinkRenewed,
} from "../../../analytics/shipmentsAnalytics";

interface ShipmentDetailViewProps {
  shipment: Shipment;
}

const DEFAULT_CENTER = { lat: 25.6866, lng: -100.3161 };
const LAST_POSITION_WAIT_MS = 1200;

const LINK_NOTICE_CLASSES = {
  expired: "border-error/30 bg-error/10 text-error",
  inactive: "border-line bg-subtle text-content-muted",
} as const;

const StatusMarker = memo(
  ({
    lat,
    lng,
    status,
    label,
    map,
  }: {
    lat: number;
    lng: number;
    status: StatusId;
    label: string;
    map: google.maps.Map | null;
  }) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);

    const markerColor = VEHICLE_STATUS_COLORS[status] || VEHICLE_STATUS_COLORS.offline;
    const markerStyle = getMarkerShape(status, null);

    useEffect(() => {
      if (!map) return;

      const div = document.createElement("div");
      div.style.position = "absolute";

      class UnitOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div: HTMLDivElement;

        constructor(position: google.maps.LatLng, element: HTMLDivElement) {
          super();
          this.position = position;
          this.div = element;
        }

        onAdd() {
          const panes = this.getPanes();
          panes?.overlayMouseTarget.appendChild(this.div);
        }

        draw() {
          const projection = this.getProjection();
          if (!projection) return;
          const pos = projection.fromLatLngToDivPixel(this.position);
          if (!pos) return;
          this.div.style.transform = "translate(-50%, -50%)";
          this.div.style.left = `${pos.x}px`;
          this.div.style.top = `${pos.y}px`;
        }

        onRemove() {
          this.div.parentNode?.removeChild(this.div);
        }
      }

      const overlay = new UnitOverlay(new google.maps.LatLng(lat, lng), div);
      overlay.setMap(map);
      overlayRef.current = overlay;
      setContainer(div);

      return () => {
        overlayRef.current?.setMap(null);
        overlayRef.current = null;
        setContainer(null);
      };
    }, [map, lat, lng]);

    if (!container) return null;

    return createPortal(
      <div
        className="flex flex-col items-center"
        style={{ width: `${MARKER_SIZE}px` }}
      >
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-0.5 whitespace-nowrap rounded border border-line bg-surface/95 px-1.5 py-0.5 text-[10px] text-content shadow-lg">
          {label}
        </div>
        <div
          className="relative"
          style={{ width: `${MARKER_SIZE}px`, height: `${MARKER_SIZE}px` }}
        >
          <VehicleMarkerGlyph
            glyph={markerStyle.glyph}
            color={markerColor}
            size={MARKER_SIZE}
            rotation={markerStyle.rotation}
          />
        </div>
      </div>,
      container,
    );
  },
);

export default function ShipmentDetailView({ shipment }: ShipmentDetailViewProps) {
  const { t, i18n } = useTranslation("envios");
  const { t: td } = useTranslation(DOCS_NS);
  // Pestañas del detalle (CC-CONSOLE #1632): seguimiento (lo de siempre) y el
  // expediente documental del envío.
  const [tab, setTab] = useState<"tracking" | "documents">("tracking");
  const unit = useShipmentUnit(shipment.actual_device_id);
  const addShipmentEvent = useAddShipmentEvent();
  const trackingLink = useShipmentTrackingLink(shipment);

  const [eventType, setEventType] = useState<ShipmentEventType>("transit");
  const [note, setNote] = useState("");
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [manualPosition, setManualPosition] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isEventsDetailModalOpen, setIsEventsDetailModalOpen] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  useEffect(() => {
    console.log("📦 Shipment data:", {
      id: shipment.id,
      contacts: shipment.contacts,
      contactsLength: shipment.contacts?.length || 0,
    });
  }, [shipment.id, shipment.contacts]);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const unitPosition = useMemo(
    () =>
      unit && unit.latitude !== null && unit.longitude !== null
        ? { lat: unit.latitude, lng: unit.longitude }
        : null,
    [unit],
  );

  const historyPosition = useMemo(() => {
    const position = shipmentPosition(shipment);
    return position ? { lat: position.lat, lng: position.long } : null;
  }, [shipment]);

  const eventPosition = unitPosition ?? historyPosition ?? DEFAULT_CENTER;

  useEffect(() => {
    setNote("");
  }, [shipment.id]);

  useEffect(() => {
    if (mapRef) mapRef.panTo(eventPosition);
  }, [mapRef, eventPosition.lat, eventPosition.lng]);

  useEffect(() => {
    if (unit?.deviceId) getLastPosition(unit.deviceId, true, unit.deviceId);
  }, [unit?.deviceId]);

  const readSocketPosition = () => {
    const vehicle = useVehiclesSocketStore
      .getState()
      .vehicles.find((item) => item.id === shipment.actual_device_id);
    if (!vehicle) return null;

    const lat = typeof vehicle.latitude === "number" ? vehicle.latitude : null;
    const long = typeof vehicle.longitude === "number" ? vehicle.longitude : null;
    if (lat === null || long === null || (lat === 0 && long === 0)) return null;

    return { lat, long, address: vehicle.address || null };
  };

  const requestFreshPosition = async () => {
    if (!unit?.deviceId) return readSocketPosition();

    getLastPosition(unit.deviceId, true, unit.deviceId);
    await new Promise((resolve) => window.setTimeout(resolve, LAST_POSITION_WAIT_MS));
    return readSocketPosition();
  };

  const handleSave = async () => {
    if (!shipment.actual_device_id) {
      toast.error(t("detail.noUnitAssigned"));
      return;
    }

    const fromStage = shipmentStage(shipment);
    // La etapa a la que lleva este tipo de evento. `eventType` viaja aparte en
    // su propia propiedad; mandarlo como `to_stage` mezclaba dos vocabularios.
    const toStage = stageForEventType(eventType);
    const backwards = isBackwardsEvent(shipment, eventType);

    if (backwards) {
      const confirmed = await confirmDialog({
        title: t("detail.backwardsTitle"),
        text: t("detail.backwardsText", {
          event: t(`eventTypes.${eventType}`),
          current: t(`eventTypes.${furthestProgressionType(shipment)}`),
        }),
        confirmText: t("detail.backwardsConfirm"),
        tone: "warning",
      });
      if (!confirmed) {
        trackShipmentStageChangeAbandoned({
          toStage,
          method: "detail_form",
        });
        return;
      }
    }

    setIsLocating(true);
    const position = await requestFreshPosition();
    setIsLocating(false);

    if (!position) {
      toast.error(t("detail.noPositionToSave"));
      return;
    }

    addShipmentEvent.mutate(
      {
        shipment,
        event: {
          event_type: eventType,
          lat: position.lat,
          long: position.long,
          address: position.address,
          note: note.trim() || null,
        },
        files: uploadedFiles,
      },
      {
        onSuccess: () => {
          // `hasEvidence` cuenta los adjuntos, no sus nombres; la nota solo
          // aporta si venia vacia o no, nunca su texto.
          trackShipmentStageChanged({
            fromStage,
            toStage,
            eventType,
            method: "detail_form",
            isBackwards: backwards,
            hasEvidence: uploadedFiles.length > 0,
          });
          setNote("");
          setUploadedFiles([]);
          toast.success(t("detail.saved"));
        },
        onError: () => {
          trackShipmentStageChangeFailed({
            toStage,
            method: "detail_form",
          });
          toast.error(t("detail.saveError"));
        },
      },
    );
  };

  const handleCopyLink = async () => {
    try {
      const url = await trackingLink.ensure();
      await navigator.clipboard.writeText(url);
      toast.success(t("detail.linkCopied"));
    } catch (error) {
      toast.error(trackingLinkErrorMessage(error, t));
    }
  };

  const handleRenewLink = async () => {
    try {
      await trackingLink.renew();
      // Nunca viaja la URL: lleva un token de acceso publico al envio.
      trackTrackingLinkRenewed();
      toast.success(t("detail.linkRenewed"));
    } catch (error) {
      toast.error(trackingLinkErrorMessage(error, t));
    }
  };

  const eventTypeOptions = SHIPMENT_EVENT_TYPES.map((value) => ({
    value,
    label: t(`eventTypes.${value}`),
  }));

  const formatEventDate = (value: string) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat(i18n.language, {
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        }).format(date);
  };

  const events = [...shipment.event_history].reverse();
  const route = [shipment.origin.address, shipment.destination.address]
    .filter(Boolean)
    .join(" → ");

  return (
    <div className="space-y-6">
      <div className="min-w-0">
        <h1 className="truncate text-xl font-bold text-content">
          {shipment.client_name}
        </h1>
        <p className="mt-0.5 flex items-center text-xs text-content-muted">
          <span className="font-mono">{shipment.tracking_number}</span>
          {route && (
            <>
              <span className="mx-2">·</span>
              <span className="inline-block truncate">{route}</span>
            </>
          )}
        </p>
      </div>

      <Tabs
        items={[
          { id: "tracking", label: td("tabs.tracking"), icon: "ri-map-pin-line" },
          { id: "documents", label: td("tabs.documents"), icon: "ri-folder-shield-2-line" },
        ]}
        activeId={tab}
        onChange={(id) => setTab(id as "tracking" | "documents")}
      />

      {tab === "documents" ? (
        <div data-testid="shipment-documents-tab">
          <DocumentChecklist subject={{ kind: "shipment", id: shipment.id }} title={shipment.tracking_number} />
        </div>
      ) : null}

      <div className={tab === "documents" ? "hidden" : "grid grid-cols-1 gap-6 xl:grid-cols-[1fr_360px]"}>
        <div className="space-y-6 rounded-2xl border border-line p-5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-content">
              <i className="ri-map-pin-line text-lg" />
              <h2>{t("detail.updateLocation")}</h2>
            </div>
            <p className="text-xs text-content-muted">{t("detail.positionFromGps")}</p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-line bg-card">
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "400px" }}
                center={eventPosition}
                zoom={11}
                onLoad={setMapRef}
                options={{
                  styles: getMapStyles(),
                  zoomControl: true,
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: false,
                }}
              >
                {mapRef && (
                  <StatusMarker
                    lat={eventPosition.lat}
                    lng={eventPosition.lng}
                    status={unit?.status ?? "offline"}
                    label={unit?.name ?? shipment.tracking_number}
                    map={mapRef}
                  />
                )}
              </GoogleMap>
            ) : (
              <div className="flex h-[400px] w-full items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-content">
                  {t("detail.eventType")}
                </label>
                <Combobox
                  value={
                    eventTypeOptions.find((option) => option.value === eventType) ?? null
                  }
                  onChange={(option) => option && setEventType(option.value)}
                  options={eventTypeOptions}
                  isSearchable={false}
                  isClearable={false}
                  aria-label={t("detail.eventType")}
                />
              </div>
              <Input
                label={t("detail.coordinates")}
                value={`${eventPosition.lat.toFixed(4)}, ${eventPosition.lng.toFixed(4)}`}
                readOnly
                className="font-mono"
              />
              <Input
                label={t("detail.assignedUnit")}
                value={unit ? `${unit.name}${unit.plate ? ` (${unit.plate})` : ""}` : t("card.noUnit")}
                readOnly
              />
            </div>

            <UnitStatusCard unit={unit} hasVehicle={!!shipment.actual_device_id} />

            <Textarea
              label={t("detail.note")}
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              placeholder={t("detail.notePlaceholder")}
              rows={4}
              hint={`${note.length}/500`}
            />

            <div>
              <label className="mb-1.5 block text-sm font-medium text-content">
                Archivos del evento
              </label>
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                disabled={uploadedFiles.length >= 5}
                className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm focus:outline-none focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  const totalFiles = uploadedFiles.length + files.length;

                  if (totalFiles > 5) {
                    toast.error(`Máximo 5 archivos permitidos. Ya tienes ${uploadedFiles.length}.`);
                    e.target.value = "";
                    return;
                  }

                  setUploadedFiles([...uploadedFiles, ...files]);
                  e.target.value = "";
                }}
              />
              <p className="text-xs text-content-muted mt-1">
                Formatos soportados: Imágenes, PDF, documentos y hojas de cálculo ({5 - uploadedFiles.length} de 5 disponibles)
              </p>

              {uploadedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-medium text-content">
                    {uploadedFiles.length} de 5 archivo{uploadedFiles.length !== 1 ? "s" : ""} seleccionado{uploadedFiles.length !== 1 ? "s" : ""}
                  </p>
                  <div className="space-y-1.5">
                    {uploadedFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between gap-2 p-2.5 bg-subtle rounded-lg"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <i className="ri-attachment-2 text-sm text-content-muted shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-content truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-content-muted">
                              {(file.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
                          }}
                          className="p-1 text-content-muted hover:text-error hover:bg-error/10 rounded-md transition-colors shrink-0"
                          title="Remover archivo"
                        >
                          <i className="ri-close-line text-lg" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                onClick={handleSave}
                loading={isLocating || addShipmentEvent.isPending}
                disabled={!shipment.actual_device_id}
                icon={<i className="ri-check-line" />}
                fullWidth
              >
                {isLocating ? t("detail.locating") : t("detail.saveUpdate")}
              </Button>
              <Button
                variant="outline"
                onClick={handleCopyLink}
                loading={trackingLink.isCreating}
                icon={<i className="ri-file-copy-line" />}
                fullWidth
              >
                {t("detail.copyTrackingLink")}
              </Button>
              {trackingLink.state === "active" && (
                <Button
                  variant="outline"
                  onClick={handleCopyLink}
                  icon={<i className="ri-file-copy-line" />}
                  fullWidth
                >
                  {t("detail.copyTrackingLink")}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <ShipmentUnitEditor shipment={shipment} />

          <CarrierTrackingCard shipment={shipment} />

          <ShipmentMerchandiseCard shipment={shipment} />

          <div className="rounded-2xl border border-line p-5">
            <div className="mb-4 flex items-center gap-2">
              <i className="ri-history-line text-lg text-content" />
              <h3 className="text-sm font-bold text-content">
                {t("detail.eventHistory")}
              </h3>
              <span className="text-xs text-content-muted">
                ({t("detail.eventsCount", { count: events.length })})
              </span>
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {events.length > 0 ? (
                events.map((event, index) => (
                  <TimelineItem
                    key={event.id}
                    event={event}
                    isCurrent={index === 0}
                    isLast={index === events.length - 1}
                    label={t(`eventTypes.${event.event_type}`, {
                      defaultValue: event.event_type,
                    })}
                    date={formatEventDate(event.date)}
                  />
                ))
              ) : (
                <EmptyState icon="ri-history-line" title={t("detail.noEvents")} />
              )}
            </div>

            {events.length > 0 && (
              <div className="mt-4 border-t border-line pt-4">
                <Button
                  variant="outline"
                  icon={<i className="ri-eye-line" />}
                  fullWidth
                  onClick={() => {
                    console.log("🔓 Opening events detail modal");
                    setIsEventsDetailModalOpen(true);
                  }}
                >
                  Ver detalles completos
                </Button>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-line p-5">
            <div className="mb-4 flex items-center gap-2">
              <i className="ri-team-line text-lg text-content" />
              <h3 className="text-sm font-bold text-content">
                Contactos
              </h3>
            </div>

            {shipment.contacts && shipment.contacts.length > 0 ? (
              <div className="space-y-2">
                {shipment.contacts.map((contact, index) => (
                  <div
                    key={contact.id}
                    className="rounded-lg bg-subtle p-3"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-sm font-semibold text-primary-500">
                        {index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-content">
                          {contact.contact_name || "Sin nombre"}
                        </p>
                        {contact.contact_email && (
                          <p className="text-xs text-content-muted truncate">
                            {contact.contact_email}
                          </p>
                        )}
                        {contact.contact_phone && (
                          <p className="text-xs text-content-muted">
                            {contact.contact_phone}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-3 rounded-lg bg-subtle p-3">
                <i className="ri-alert-line text-lg text-content-muted" />
                <p className="text-xs text-content-muted">
                  Sin contactos asignados
                </p>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-line p-5">
            <h3 className="mb-3 text-sm font-bold text-content">
              {t("detail.trackingLink")}
            </h3>
            {trackingLink.isLoading ? (
              <div className="flex h-10 items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-xl text-primary-500" />
              </div>
            ) : trackingLink.state === "active" && trackingLink.url ? (
              <div className="flex gap-2">
                <Input value={trackingLink.url} readOnly className="font-mono" />
                <Button
                  variant="outline"
                  iconOnly
                  icon={<i className="ri-file-copy-line" />}
                  onClick={handleCopyLink}
                  aria-label={t("detail.copyTrackingLink")}
                />
              </div>
            ) : trackingLink.state === "missing" ? (
              <div className="space-y-3">
                <p className="text-xs text-content-muted">
                  {trackingLink.hasUnit
                    ? t("detail.noTrackingLink")
                    : t("detail.trackingLinkNeedsUnit")}
                </p>
                {trackingLink.hasUnit && (
                  <Button
                    variant="outline"
                    size="sm"
                    loading={trackingLink.isCreating}
                    icon={<i className="ri-link" />}
                    onClick={handleCopyLink}
                  >
                    {t("detail.createTrackingLink")}
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div
                  className={`rounded-xl border px-3 py-2 text-xs ${
                    LINK_NOTICE_CLASSES[
                      trackingLink.state === "expired" ? "expired" : "inactive"
                    ]
                  }`}
                >
                  {trackingLink.state === "expired"
                    ? t("detail.trackingLinkExpired")
                    : t("detail.trackingLinkInactive")}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  loading={trackingLink.isRenewing}
                  icon={<i className="ri-refresh-line" />}
                  onClick={handleRenewLink}
                >
                  {t("detail.renewTrackingLink")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <EventsDetailModal
        isOpen={isEventsDetailModalOpen}
        events={shipment.event_history}
        onClose={() => setIsEventsDetailModalOpen(false)}
      />
    </div>
  );
}

function UnitStatusCard({
  unit,
  hasVehicle,
}: {
  unit: ShipmentUnit | null;
  hasVehicle: boolean;
}) {
  const { t } = useTranslation("envios");

  if (!hasVehicle) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-line bg-subtle p-3">
        <i className="ri-truck-line text-lg text-content-muted" />
        <p className="text-xs text-content-muted">{t("detail.noUnitAssigned")}</p>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-line bg-subtle p-3">
        <i className="ri-signal-wifi-error-line text-lg text-content-muted" />
        <p className="text-xs text-content-muted">{t("detail.unitNotReporting")}</p>
      </div>
    );
  }

  const hasPosition = unit.latitude !== null && unit.longitude !== null;

  return (
    <div className="flex items-center gap-3 rounded-lg bg-subtle p-3">
      <div className="relative shrink-0">
        <UnitAvatar
          iconId={unit.iconId}
          name={unit.name}
          toneClassName="bg-app"
          fallback={<i className="ri-truck-line text-content-muted" />}
        />
        <span
          className={`absolute bottom-0.5 right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-subtle ${STATUS_META[unit.status].dot}`}
        />
      </div>

      <div className="flex w-full items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="truncate text-sm font-bold text-content">{unit.name}</h4>
            <Badge tone={UNIT_STATUS_TONES[unit.status]} size="sm">
              {t(`unitStatus.${unit.status}`)}
            </Badge>
          </div>
          <p className="mt-0.5 truncate text-xs text-content-secondary">
            {[unit.driver || t("card.noDriver"), unit.plate].filter(Boolean).join(" · ")}
          </p>
          <p className="mt-0.5 truncate font-mono text-xs text-content-muted">
            {hasPosition
              ? `${unit.latitude!.toFixed(4)}, ${unit.longitude!.toFixed(4)}`
              : t("detail.noPosition")}
            {unit.speed !== null && ` · ${unit.speed.toFixed(0)} km/h`}
            {unit.signal !== null && ` · ${t("detail.signal")}: ${unit.signal}`}
            {` · ${getTimeAgo(unit.lastReportSeconds)}`}
          </p>
        </div>

        {hasPosition && (
          <Badge tone="success" size="sm">
            <i className="ri-focus-3-line" /> {t("detail.followingGps")}
          </Badge>
        )}
      </div>
    </div>
  );
}

function TimelineItem({
  event,
  isCurrent,
  isLast,
  label,
  date,
}: {
  event: ShipmentEvent;
  isCurrent: boolean;
  isLast: boolean;
  label: string;
  date: string;
}) {
  return (
    <div className="flex gap-2">
      <div className="relative flex flex-col items-center">
        <div
          className={`mt-1.5 h-3 w-3 rounded-full ${isCurrent ? "bg-primary-500" : "bg-line"}`}
        />
        {!isLast && <div className="absolute top-5 h-full w-0.5 bg-line" />}
      </div>

      <div className="min-w-0 pb-4 pt-0.5">
        <p className="text-sm font-bold text-content">{label}</p>
        <p className="mt-0.5 text-xs text-content-muted">
          {[date, event.address].filter(Boolean).join(" · ")}
        </p>
        {event.note && (
          <p className="mt-1 text-xs italic text-content-secondary">{event.note}</p>
        )}
      </div>
    </div>
  );
}
